import { useParams } from 'wouter';
import { useState, useEffect, useRef } from 'react';
import { AdminProjects } from '@platform/client';
import { Button } from '@platform/components';
import ProjectPublishStageModal from './ProjectPublishStageModal';
import cx from 'classix';
import FlexibleLink from '../../../util/FlexibleLink';
import { ReviewPageWrapper, ReviewItem } from '../../AdminViewPage';

export const ProjectReview = () => {
  const { id } = useParams();
  const [publishStageModalIsOpen, setPublishStageModalIsOpen] = useState(false);
  const {
    data: project,
    isError,
    isLoading,
    isSuccess,
  } = AdminProjects.useGetReviewProject(Number(id));

  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!publishStageModalIsOpen) {
      topRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [publishStageModalIsOpen]);

  if (!project) {
    return null;
  }

  return (
    <ReviewPageWrapper
      title={project.name}
      isLoading={isLoading}
      isError={isError}
      isSuccess={isSuccess}
      topRef={topRef}
    >
      <div
        className={cx(
          'max-w-1/2 text-lg text-center mx-auto rounded-md px-2 italic',
          project.publishingStage === 'published' && 'bg-success-500 text-white',
          (project.publishingStage === 'review' ||
            project.publishingStage === 'revision_requested') &&
            'bg-warning-100',
          (project.publishingStage === 'draft' || project.publishingStage === 'unpublished') &&
            'bg-gray-200',
        )}
      >
        <h2>Current Stage: {project.publishingStage}</h2>
      </div>
      <ReviewItem label='Organization'>
        <FlexibleLink
          type='legacy'
          href={`/organization/${project.organizationId}`}
          className='text-brand-500 hover:underline'
          newTab={true}
        >
          {project.organizationName}
        </FlexibleLink>
      </ReviewItem>
      <ReviewItem label='Timezone'>{project.timezone}</ReviewItem>
      <ReviewItem label='Skills'>{project.expertises}</ReviewItem>
      <ReviewItem label='Language(s)'>{project.language}</ReviewItem>
      <ReviewItem label='Workload / Role'>
        {project.workload} / {project.roleType}
      </ReviewItem>
      <ReviewItem label='Open to Teams'>{project.openToTeam ? 'Yes' : 'No'}</ReviewItem>
      <ReviewItem label='Description'>
        <p
          dangerouslySetInnerHTML={{
            __html: project.description ? project.description : 'Not Provided',
          }}
        ></p>
      </ReviewItem>
      <ReviewItem label='Goals'>
        <p
          dangerouslySetInnerHTML={{
            __html: project.goals ? project.goals : 'Not Provided',
          }}
        ></p>
      </ReviewItem>
      <ReviewItem label='Business Challenge'>{project.businessType}</ReviewItem>
      <ReviewItem label='Other Requirements'>
        <p
          dangerouslySetInnerHTML={{
            __html: project.otherRequirements ? project.otherRequirements : 'Not Provided',
          }}
        ></p>
      </ReviewItem>
      <ReviewItem label='Benefits'>{project.benefits}</ReviewItem>
      <ReviewItem label='Learning Outcomes'>
        <p
          dangerouslySetInnerHTML={{
            __html: project.learningOutcomes ? project.learningOutcomes : 'Not Provided',
          }}
        ></p>
      </ReviewItem>
      <div className='flex justify-center gap-2'>
        <Button variant='secondary-color' size='md' href='/platform/admin/projects'>
          Back to Projects
        </Button>
        <Button
          variant='primary'
          size='md'
          onPress={() => {
            setPublishStageModalIsOpen(true);
          }}
        >
          Update Publish Stage
        </Button>
      </div>
      <ProjectPublishStageModal
        isOpen={publishStageModalIsOpen}
        project={project}
        onClose={() => {
          setPublishStageModalIsOpen(false);
        }}
      />
    </ReviewPageWrapper>
  );
};
